import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { toast } from "sonner";
import { Search, ExternalLink, Radio, Loader2 } from "lucide-react";

const PAGE_SIZE = 40; // 321 items paginated — 40 per page → 9 pages max

/**
 * Podcast archive — 321 quotes ordered by episode number.
 * Provides a search box (matches title, quote text, explanation and the #N number)
 * and paginates the results for readability.
 */
export default function PodcastsPage() {
  const { t, lang } = useI18n();

  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null); // podcast number that is expanded, or null
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/podcasts/quotes", { params: { lang } });
        if (!cancelled) setAll(data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Podcasts laden mislukt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    // Support "#42" style searches
    const numMatch = q.match(/^#?\s*(\d+)$/);
    if (numMatch) {
      const n = Number(numMatch[1]);
      return all.filter((x) => x.number === n);
    }
    return all.filter((x) => {
      return (
        x.title.toLowerCase().includes(q) ||
        x.quote.toLowerCase().includes(q) ||
        x.explanation.toLowerCase().includes(q)
      );
    });
  }, [all, query]);

  // Reset page when the query changes
  useEffect(() => {
    setPage(1);
    setExpanded(null);
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <main
      data-testid="podcasts-page"
      className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24"
    >
      <p className="label-small text-[#8B3A3A] mb-4 flex items-center gap-2">
        <Radio size={12} /> {t("podcasts.eyebrow")}
      </p>
      <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918] mb-6">
        {t("podcasts.title")}
      </h1>
      <p className="text-[#4A4238] max-w-2xl mb-10 leading-relaxed">{t("podcasts.intro")}</p>

      {/* Search */}
      <div className="mb-6 flex items-center gap-3 border border-[#1A1918] bg-[#F4EEDF] px-4 py-3 focus-within:border-[#8B3A3A]">
        <Search size={14} className="text-[#8B3A3A]" />
        <input
          type="search"
          data-testid="podcasts-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("podcasts.search_placeholder")}
          className="flex-1 bg-transparent outline-none text-[#1A1918] placeholder:text-[#7A7061] text-sm"
        />
        <span
          data-testid="podcasts-count"
          className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#7A7061] whitespace-nowrap"
        >
          {t("podcasts.results_count", { n: filtered.length, total: all.length })}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-[#4A4238] py-16">
          <Loader2 size={16} className="animate-spin text-[#8B3A3A]" />
          <span>{t("common.loading")}</span>
        </div>
      ) : filtered.length === 0 ? (
        <p
          data-testid="podcasts-empty"
          className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 text-center italic text-[#4A4238]"
        >
          {t("podcasts.no_results")}
        </p>
      ) : (
        <>
          <ul className="border border-[#C1B49A]" data-testid="podcasts-list">
            {visible.map((p, i) => (
              <PodcastRow
                key={p.number}
                p={p}
                first={i === 0}
                isExpanded={expanded === p.number}
                onToggle={() => setExpanded(expanded === p.number ? null : p.number)}
                t={t}
              />
            ))}
          </ul>
          {totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
            />
          )}
        </>
      )}
    </main>
  );
}

function PodcastRow({ p, first, isExpanded, onToggle, t }) {
  return (
    <li
      data-testid={`podcast-row-${p.number}`}
      className={`${first ? "" : "border-t border-[#C1B49A]"} bg-[#EAE0CA]/30`}
    >
      <button
        type="button"
        data-testid={`podcast-row-toggle-${p.number}`}
        onClick={onToggle}
        className="w-full flex items-center gap-4 px-4 md:px-6 py-3 text-left hover:bg-[#EAE0CA]/70 transition-colors"
      >
        <span className="font-mono text-[11px] tracking-[0.2em] text-[#8B3A3A] font-semibold w-14 shrink-0">
          #{String(p.number).padStart(3, "0")}
        </span>
        <span className="font-display text-[#1A1918] flex-1 truncate">
          {p.title.replace(/^#\d+\s*/, "")}
        </span>
        <span className="text-[10px] italic text-[#7A7061] hidden md:block">
          {isExpanded ? "▴" : "▾"}
        </span>
      </button>
      {isExpanded && (
        <div className="px-4 md:px-6 pb-6 pt-1 bg-[#F4EEDF] border-t border-[#C1B49A]">
          <p className="label-small text-[#8B3A3A] mb-2 mt-4">{t("podcasts.quote_label")}</p>
          <blockquote className="font-display text-lg md:text-xl italic text-[#1A1918] leading-snug mb-5 max-w-3xl">
            {p.quote}
          </blockquote>
          <p className="label-small text-[#8B3A3A] mb-2">{t("podcasts.explanation_label")}</p>
          <p className="text-[15px] text-[#4A4238] leading-relaxed max-w-3xl mb-6">
            {p.explanation}
          </p>
          {p.spotify_url && (
            <a
              href={p.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`podcast-spotify-${p.number}`}
              className="btn-primary inline-flex items-center gap-2 text-sm"
            >
              {t("podcasts.spotify_cta")} <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}
    </li>
  );
}

function Pagination({ page, totalPages, onChange }) {
  const nums = paginationRange(page, totalPages);
  return (
    <nav
      data-testid="podcasts-pagination"
      className="mt-6 flex flex-wrap items-center gap-2 justify-center"
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        data-testid="podcasts-prev-btn"
        className="btn-ghost text-xs disabled:opacity-30"
      >
        ‹
      </button>
      {nums.map((n, i) =>
        n === "…" ? (
          <span key={`e-${i}`} className="text-[#7A7061] px-1">
            …
          </span>
        ) : (
          <button
            key={n}
            type="button"
            data-testid={`podcasts-page-btn-${n}`}
            onClick={() => onChange(n)}
            className={`min-w-[36px] px-2 py-1 text-xs font-mono tracking-widest border ${
              n === page
                ? "border-[#8B3A3A] text-[#8B3A3A] bg-[#EAE0CA]"
                : "border-[#C1B49A] text-[#4A4238] hover:border-[#8B3A3A]"
            }`}
          >
            {n}
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        data-testid="podcasts-next-btn"
        className="btn-ghost text-xs disabled:opacity-30"
      >
        ›
      </button>
    </nav>
  );
}

function paginationRange(cur, total) {
  // 1 … cur-1 cur cur+1 … total
  const nums = new Set([1, total, cur, cur - 1, cur + 1]);
  const sorted = [...nums].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}
